import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Rendered by tests with tsx, which would fall back to the classic JSX runtime: pin the automatic one (as tsc does).
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import ZoomIn from 'lucide-react/dist/esm/icons/zoom-in.mjs';
import { OutfitCard, useReducedMotionPreference } from './OutfitCard.js';
/**
 * The hover preview on every outfit tile (owner, 2026-10-11: "a little button on each tile that when hovered over shows a
 * much larger preview of each outfit they are too small"). A small magnifier button sits on the tile; hovering or
 * focusing it (or tapping it, on touch) opens a large preview of the outfit, up to MASCOT_PREVIEW_SIDE pixels, beside
 * the tile, placed so it never leaves the window; moving the pointer off, blurring, or Escape closes it. The button is
 * a sibling of the tile, never inside it, so opening a preview never picks (and a tile stays one button). The full
 * image is read only when a preview opens, once per image hash, through the person image route; until it lands (or when
 * it cannot be read) the preview shows the tile's own thumbnail, scaled up.
 *
 * An outfit that has animations (`hasActions`, with a reader for its preview) plays them in the enlargement (owner,
 * 2026-10-19: "i want users that have animated outfits to be able to preview the animation when they hover over the
 * enlargement of the outfit"): while the pointer is over the enlargement its primary clip loops in place of the still
 * art, and the still art returns when the pointer leaves. The clip is one sprite sheet (GET
 * /v1/mascot/outfits/:id/actions-preview), read once per outfit for the page, the first time it is needed. So the
 * pointer can reach the enlargement, the magnifier of such an outfit keeps the preview open for MASCOT_PREVIEW_LEAVE_MS
 * after the pointer leaves it. Without a mouse, or when the person asks for less motion (the browser's
 * prefers-reduced-motion, or the app's reduced-motion setting), a Play button under the art plays and stops the clip;
 * the pointer never starts it then. Every other outfit's enlargement is exactly as before.
 *
 * A read of the clip that fails (#616: the first read of an outfit whose preview gyro-svc has not made yet can outlast the
 * desktop app's 30 s forward while other outfits' previews are made one at a time; gyro-svc finishes it and keeps it) leaves
 * the still art and says the animation is unavailable; MASCOT_CLIP_RETRY_MS later the hover (or Play) reads it again, at
 * most MASCOT_CLIP_RETRIES times for the tile, after which it stays unavailable until the gallery is opened again.
 */
export const MASCOT_PREVIEW_SIDE = 512;
/** A hover waits this long before opening, so a pointer crossing the gallery does not open one preview after another. */
export const MASCOT_PREVIEW_HOVER_MS = 120;
/** How long an animated outfit's enlargement stays open after the pointer leaves its magnifier, for the pointer to reach it. */
export const MASCOT_PREVIEW_LEAVE_MS = 250;
const PREVIEW_MARGIN = 8;
const PREVIEW_GAP = 10;
/** The caption under the image, and the preview's padding, in pixels: what the box adds to the image's side. */
const PREVIEW_CHROME = 44;
/** Full images kept in this page, by hash (up to 1 MB each). */
const IMAGES_KEPT_MAX = 12;
/** Animation previews kept in this page, by outfit (up to 8 MB of base64 each: frames of 256 px, 2026-10-21; 32 MB at most). */
const CLIPS_KEPT_MAX = 4;
/** After a failed read of an outfit's animation preview, the wait before the hover (or Play) reads it again (#616). */
export const MASCOT_CLIP_RETRY_MS = 15_000;
/** How many times a tile reads a failed animation preview again before it stays unavailable (until the gallery is opened again). */
export const MASCOT_CLIP_RETRIES = 3;
const imagesRead = new Map();
const clipsRead = new Map();
function rememberImage(sha256, dataUri) {
    imagesRead.delete(sha256);
    imagesRead.set(sha256, dataUri);
    if (imagesRead.size > IMAGES_KEPT_MAX)
        imagesRead.delete(imagesRead.keys().next().value);
}
function rememberClip(outfitId, clip) {
    clipsRead.delete(outfitId);
    clipsRead.set(outfitId, clip);
    if (clipsRead.size > CLIPS_KEPT_MAX)
        clipsRead.delete(clipsRead.keys().next().value);
}
/** Test seam: forget every image and animation preview read. */
export function __forgetPreviewImagesForTests() {
    imagesRead.clear();
    clipsRead.clear();
}
/**
 * Where a preview of `side` pixels goes for a tile at `anchor` in a window of `viewportWidth` by `viewportHeight`: to the
 * right of the tile when there is room, else to the left, else below or above it, and always clamped inside the window
 * with PREVIEW_MARGIN to spare, shrunk when the window is smaller than the preview. Pure, for its tests.
 */
export function placeMascotPreview(anchor, side, viewportWidth, viewportHeight) {
    const width = Math.max(96, Math.min(side, viewportWidth - 2 * PREVIEW_MARGIN, viewportHeight - 2 * PREVIEW_MARGIN - PREVIEW_CHROME));
    const height = width + PREVIEW_CHROME;
    const clampX = (x) => Math.min(Math.max(PREVIEW_MARGIN, x), Math.max(PREVIEW_MARGIN, viewportWidth - PREVIEW_MARGIN - width));
    const clampY = (y) => Math.min(Math.max(PREVIEW_MARGIN, y), Math.max(PREVIEW_MARGIN, viewportHeight - PREVIEW_MARGIN - height));
    const middle = (anchor.top + anchor.bottom) / 2 - height / 2;
    if (anchor.right + PREVIEW_GAP + width <= viewportWidth - PREVIEW_MARGIN)
        return { left: anchor.right + PREVIEW_GAP, top: clampY(middle), width };
    if (anchor.left - PREVIEW_GAP - width >= PREVIEW_MARGIN)
        return { left: anchor.left - PREVIEW_GAP - width, top: clampY(middle), width };
    // Neither side has room (a narrow window): below the tile when that fits, else above, else wherever it fits best.
    const centred = clampX((anchor.left + anchor.right) / 2 - width / 2);
    if (anchor.bottom + PREVIEW_GAP + height <= viewportHeight - PREVIEW_MARGIN)
        return { left: centred, top: anchor.bottom + PREVIEW_GAP, width };
    if (anchor.top - PREVIEW_GAP - height >= PREVIEW_MARGIN)
        return { left: centred, top: anchor.top - PREVIEW_GAP - height, width };
    return { left: centred, top: clampY(middle), width };
}
/** The Popover API where the browser has it (Chromium 114+, so every WebView2 the agent ships); nothing elsewhere (jsdom). */
function showInTopLayer(element, show) {
    if (!element)
        return;
    try {
        if (show && typeof element.showPopover === 'function')
            element.showPopover();
        else if (!show && typeof element.hidePopover === 'function')
            element.hidePopover();
    }
    catch {
        /* already in that state, or not a popover here */
    }
}
/**
 * The frame window's size for a clip of `frameWidth` x `frameHeight` in a box of `width` x `height` CSS pixels on a screen of
 * `devicePixelRatio`: the largest size that keeps the frame's shape, in whole device pixels (owner, 2026-10-21: the clip
 * in the enlargement "gets super blurry"; a window of fractional device pixels has the sheet resampled a second time, to
 * the pixel grid, on a 1.25x, 1.5x or 2x screen). On a 1x screen that is whole CSS pixels, as before. Pure, for its tests.
 */
export function mascotClipWindow(clip, box, devicePixelRatio) {
    const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
    const scale = Math.min(box.width / clip.frameWidth, box.height / clip.frameHeight);
    const snap = (px) => Math.max(1, Math.round(px * dpr)) / dpr;
    return { width: snap(clip.frameWidth * scale), height: snap(clip.frameHeight * scale) };
}
/**
 * The clip, looping: the sprite sheet behind a window one frame wide, stepped along at the clip's fps by moving the sheet
 * (never a re-render a frame). Sized to fit the art's box, keeping the frame's shape, in whole device pixels
 * (mascotClipWindow); the sheet is `columns` windows wide, so every frame's edge lands on the pixel grid too, and it is
 * resampled once, smoothly (`image-rendering: auto`, gyro.css). `data-frame` names the frame shown.
 */
export function MascotActionClip({ clip, name }) {
    const frameRef = useRef(null);
    const sheetRef = useRef(null);
    const [size, setSize] = useState({ width: clip.frameWidth, height: clip.frameHeight });
    const rows = Math.ceil(clip.frameCount / clip.columns);
    useLayoutEffect(() => {
        const box = frameRef.current?.parentElement?.getBoundingClientRect();
        if (!box || box.width <= 0 || box.height <= 0)
            return;
        setSize(mascotClipWindow(clip, box, typeof window === 'undefined' ? 1 : window.devicePixelRatio));
    }, [clip]);
    useEffect(() => {
        let frame = 0;
        const timer = setInterval(() => {
            frame = (frame + 1) % clip.frameCount;
            const sheet = sheetRef.current;
            if (sheet) {
                sheet.style.left = `${-(frame % clip.columns) * 100}%`;
                sheet.style.top = `${-Math.floor(frame / clip.columns) * 100}%`;
            }
            frameRef.current?.setAttribute('data-frame', String(frame));
        }, Math.max(16, Math.round(1000 / clip.fps)));
        return () => clearInterval(timer);
    }, [clip]);
    return _jsx("span", { className: "gyro-mascot-preview-clip", ref: frameRef, role: "img", "aria-label": `${name}, animating`, "data-frame": "0", style: { width: size.width, height: size.height }, children: _jsx("img", { ref: sheetRef, src: `data:image/png;base64,${clip.sheetPng}`, alt: "", draggable: false, style: { width: `${clip.columns * 100}%`, height: `${rows * 100}%`, left: 0, top: 0 } }) });
}
export function MascotOutfitPreview({ outfitId, name, thumbnail, sha256, loadImage, tabIndex = -1, card, hasActions, loadActionsPreview }) {
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState(null);
    const [image, setImage] = useState(() => (sha256 ? imagesRead.get(sha256) : undefined));
    const [failed, setFailed] = useState(false);
    const buttonRef = useRef(null);
    const popoverRef = useRef(null);
    const hoverTimer = useRef(null);
    const leaveTimer = useRef(null);
    const popoverId = `${useId()}-preview`;
    // The animation (an outfit that has one, with a reader): hovering the enlargement plays it, Play pins it.
    const animated = hasActions === true && loadActionsPreview !== undefined;
    const still = useReducedMotionPreference() || card?.reducedMotion === true;
    const [clip, setClip] = useState(() => clipsRead.get(outfitId) ?? null);
    // The last read failed: the still art, "unavailable", Play disabled; cleared MASCOT_CLIP_RETRY_MS later while retries remain.
    const [clipFailed, setClipFailed] = useState(false);
    const clipRetries = useRef(0);
    const clipRetryTimer = useRef(null);
    const [hovering, setHovering] = useState(false);
    const [pinned, setPinned] = useState(false);
    const playing = open && animated && (pinned || (hovering && !still));
    const cancelHover = useCallback(() => {
        if (hoverTimer.current !== null) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    }, []);
    const cancelLeave = useCallback(() => {
        if (leaveTimer.current !== null) {
            clearTimeout(leaveTimer.current);
            leaveTimer.current = null;
        }
    }, []);
    const show = useCallback(() => {
        cancelHover();
        cancelLeave();
        const cell = buttonRef.current?.closest('.gyro-mascot-cell') ?? buttonRef.current;
        if (!cell || typeof window === 'undefined')
            return;
        setPlacement(placeMascotPreview(cell.getBoundingClientRect(), MASCOT_PREVIEW_SIDE, window.innerWidth, window.innerHeight));
        setOpen(true);
    }, [cancelHover, cancelLeave]);
    const hide = useCallback(() => {
        cancelHover();
        cancelLeave();
        setOpen(false);
        setHovering(false);
        setPinned(false);
    }, [cancelHover, cancelLeave]);
    useEffect(() => () => {
        cancelHover();
        cancelLeave();
        if (clipRetryTimer.current !== null)
            clearTimeout(clipRetryTimer.current);
    }, [cancelHover, cancelLeave]);
    // In the browser's top layer when it has one (nothing scrolls or clips it); the page's scroll or a resize closes it,
    // since its place was measured once.
    useEffect(() => {
        if (!open)
            return undefined;
        const popover = popoverRef.current;
        showInTopLayer(popover, true);
        const close = () => hide();
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            showInTopLayer(popover, false);
        };
    }, [open, hide]);
    // The full image, read once a preview opens and kept by hash for the page.
    useEffect(() => {
        if (!open || image !== undefined || failed || !loadImage)
            return undefined;
        const known = sha256 ? imagesRead.get(sha256) : undefined;
        if (known) {
            setImage(known);
            return undefined;
        }
        let live = true;
        void loadImage(outfitId).then((full) => {
            const uri = `data:image/png;base64,${full.imagePng}`;
            rememberImage(full.sha256, uri);
            if (sha256 && sha256 !== full.sha256)
                rememberImage(sha256, uri);
            if (live)
                setImage(uri);
        }, () => { if (live)
            setFailed(true); });
        return () => { live = false; };
    }, [open, image, failed, loadImage, outfitId, sha256]);
    // The animation preview, read the first time it is to play and kept by outfit for the page. A failed read is read again
    // MASCOT_CLIP_RETRY_MS later, when it is (still, or again) to play, at most MASCOT_CLIP_RETRIES times (#616).
    useEffect(() => {
        if (!playing || clip !== null || clipFailed || !loadActionsPreview)
            return undefined;
        const known = clipsRead.get(outfitId);
        if (known) {
            setClip(known);
            return undefined;
        }
        let live = true;
        void loadActionsPreview(outfitId).then((read) => {
            rememberClip(outfitId, read);
            if (live)
                setClip(read);
        }, () => {
            if (!live)
                return;
            setClipFailed(true);
            if (clipRetries.current >= MASCOT_CLIP_RETRIES)
                return;
            clipRetries.current += 1;
            clipRetryTimer.current = setTimeout(() => {
                clipRetryTimer.current = null;
                setClipFailed(false);
            }, MASCOT_CLIP_RETRY_MS);
        });
        return () => { live = false; };
    }, [playing, clip, clipFailed, loadActionsPreview, outfitId]);
    const focusInside = () => {
        const active = buttonRef.current?.ownerDocument.activeElement ?? null;
        return active !== null && (active === buttonRef.current || popoverRef.current?.contains(active) === true);
    };
    const onMouseEnter = () => {
        cancelLeave();
        if (open || hoverTimer.current !== null)
            return;
        hoverTimer.current = setTimeout(() => { hoverTimer.current = null; show(); }, MASCOT_PREVIEW_HOVER_MS);
    };
    const onMouseLeave = () => {
        cancelHover();
        // Keyboard focus keeps the preview up; a pointer leaving closes it. An animated outfit's stays a moment, so the
        // pointer can reach the enlargement.
        if (focusInside())
            return;
        if (animated) {
            cancelLeave();
            leaveTimer.current = setTimeout(() => { leaveTimer.current = null; hide(); }, MASCOT_PREVIEW_LEAVE_MS);
        }
        else {
            hide();
        }
    };
    const onPopoverEnter = () => {
        cancelLeave();
        if (animated)
            setHovering(true);
    };
    const onPopoverLeave = () => {
        setHovering(false);
        if (!focusInside())
            hide();
    };
    const onBlur = (event) => {
        // Focus moving between the magnifier and the enlargement's Play button keeps it open; leaving both closes it.
        const next = event.relatedTarget;
        if (next && (next === buttonRef.current || popoverRef.current?.contains(next)))
            return;
        hide();
    };
    const onClick = (event) => {
        // A tap on touch: toggles; never reaches the tile, so it never picks.
        event.preventDefault();
        event.stopPropagation();
        if (open)
            hide();
        else
            show();
    };
    const onKeyDown = (event) => {
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            hide();
            buttonRef.current?.focus();
        }
    };
    const togglePinned = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setPinned((current) => !current);
    };
    const style = placement ? { position: 'fixed', left: placement.left, top: placement.top, width: placement.width } : undefined;
    const shown = image ?? thumbnail;
    const clipNode = playing && clip ? _jsx(MascotActionClip, { clip: clip, name: name }) : undefined;
    const status = _jsxs(_Fragment, { children: [!image && !failed && loadImage && _jsx("span", { className: "gyro-mascot-preview-status", role: "status", children: "Loading full size\u2026" }), failed && _jsx("span", { className: "gyro-mascot-preview-status", children: "Full size unavailable" }), animated && _jsxs("span", { className: "gyro-mascot-preview-motion", children: [playing && !clip && !clipFailed && _jsx("span", { className: "gyro-mascot-preview-status", role: "status", children: "Loading animation\u2026" }), clipFailed && _jsx("span", { className: "gyro-mascot-preview-status", children: "Animation preview unavailable" }), !playing && !still && !clipFailed && _jsx("span", { className: "gyro-mascot-preview-status gyro-mascot-preview-hint", children: "Hover to preview the animation" }), _jsx("button", { type: "button", className: "gyro-mascot-preview-play", "aria-pressed": pinned, onClick: togglePinned, disabled: clipFailed, children: pinned ? 'Stop' : 'Play animation' })] })] });
    return _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "gyro-mascot-tile-preview", ref: buttonRef, "aria-label": `Preview ${name}`, title: "Preview", "aria-expanded": open, tabIndex: tabIndex, "aria-controls": open ? popoverId : undefined, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onFocus: show, onBlur: onBlur, onClick: onClick, onKeyDown: onKeyDown, children: _jsx(ZoomIn, { size: 12, "aria-hidden": "true" }) }), open && _jsx("div", { id: popoverId, className: `gyro-mascot-preview${card ? ' gyro-mascot-preview-card' : ''}${playing && clip ? ' gyro-mascot-preview-playing' : ''}`, role: "tooltip", ref: popoverRef, popover: "manual", style: style, onMouseEnter: onPopoverEnter, onMouseLeave: onPopoverLeave, onBlur: onBlur, onKeyDown: onKeyDown, children: card
                    ? _jsx(OutfitCard, { name: name, art: shown, artNode: clipNode, economy: card.economy, hasActions: card.hasActions, collection: card.collection, reducedMotion: card.reducedMotion, className: image ? '' : 'gyro-outfit-card-art-small', status: _jsx("span", { className: "gyro-mascot-preview-caption", children: status }) })
                    : _jsxs(_Fragment, { children: [_jsx("span", { className: `gyro-mascot-preview-art${image ? '' : ' gyro-mascot-preview-art-small'}`, children: clipNode ?? (shown ? _jsx("img", { src: shown, alt: name, draggable: false }) : null) }), _jsxs("span", { className: "gyro-mascot-preview-caption", children: [_jsx("span", { className: "gyro-mascot-preview-name", children: name }), status] })] }) })] });
}
//# sourceMappingURL=MascotOutfitPreview.js.map